// Detalle de una cotización con acción "enviar" para generar el link público.

import { useState } from 'react';

import { quotesApi } from '../api';
import { useSendQuote } from '../hooks/useQuotes';
import type { Quote } from '../types';

interface Props {
  quote: Quote;
  onUpdated?: (quote: Quote) => void;
}

export function QuoteDetail({ quote, onUpdated }: Props) {
  const { send, loading, error } = useSendQuote();
  const [current, setCurrent] = useState<Quote>(quote);

  const handleSend = async () => {
    const res = await send(current.id);
    if (res) {
      const updated: Quote = {
        ...current,
        status: res.status,
        public_url: res.public_url,
        public_token: res.public_token,
      };
      setCurrent(updated);
      onUpdated?.(updated);
    }
  };

  return (
    <div className="p-4 border rounded-md space-y-4 max-w-3xl">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">{current.title || 'Cotización'}</h3>
          <div className="text-xs text-muted-foreground font-mono">{current.id}</div>
        </div>
        <span className="px-2 py-1 text-xs rounded bg-muted">{current.status}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Cliente</div>
          <div>{current.client_name || '—'}</div>
          <div className="text-xs text-muted-foreground">{current.client_email}</div>
          <div className="text-xs text-muted-foreground">{current.client_phone}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="text-2xl font-semibold font-mono">
            {current.currency} {Number(current.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-muted-foreground">
            Subtotal {Number(current.subtotal).toFixed(2)} · IVA {Number(current.tax_total).toFixed(2)}
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs text-muted-foreground mb-1">Líneas</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b">
              <th className="px-2 py-1">Descripción</th>
              <th className="px-2 py-1 text-right">Cant.</th>
              <th className="px-2 py-1 text-right">Unitario</th>
              <th className="px-2 py-1 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {current.line_items.map((li) => (
              <tr key={li.id} className="border-b">
                <td className="px-2 py-1">{li.description}</td>
                <td className="px-2 py-1 text-right font-mono">{li.qty}</td>
                <td className="px-2 py-1 text-right font-mono">{Number(li.unit_price).toFixed(2)}</td>
                <td className="px-2 py-1 text-right font-mono">
                  {(Number(li.qty) * Number(li.unit_price)).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {current.notes && (
        <div className="text-sm">
          <div className="text-xs text-muted-foreground">Notas</div>
          <div className="whitespace-pre-wrap">{current.notes}</div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <a
          href={quotesApi.pdfUrl(current.id)}
          target="_blank"
          rel="noreferrer"
          className="px-3 py-1.5 text-sm border rounded"
        >
          Ver PDF
        </a>
        {current.status === 'draft' && (
          <button
            type="button"
            onClick={handleSend}
            disabled={loading}
            className="px-3 py-1.5 text-sm rounded bg-black text-primary-foreground disabled:opacity-50"
          >
            {loading ? 'Enviando…' : 'Marcar como enviada'}
          </button>
        )}
        {current.public_url && current.status !== 'draft' && (
          <code className="text-xs text-neutral-700 bg-muted px-2 py-1 rounded">
            {current.public_url}
          </code>
        )}
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}
    </div>
  );
}
